import React from 'react';
import { Link } from 'react-router-dom';
import { Volume2, ArrowLeft, CheckCircle, Mic, Speaker, Radio } from 'lucide-react';
import QuoteForm from '../components/QuoteForm';

const TechAudioPage = () => {
  const images = {
    audio: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fbulletcam_audio_4cbd31a7f2%402.jpg",
    integration: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Feasy_integration_d3864d827c%402.jpg",
  };

  return (
    <div className="min-h-screen py-8 md:py-12" data-testid="tech-audio-page">
      {/* Breadcrumb */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-12 mb-8">
        <Link to="/superior-video" className="inline-flex items-center gap-2 text-white/50 hover:text-white transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span>Retour à la vidéosurveillance</span>
        </Link>
      </div>

      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-12">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-500/20 border border-green-500/40 rounded-full mb-4">
              <Volume2 className="w-4 h-4 text-green-400" />
              <span className="text-sm text-green-400 font-semibold">Communication intégrée</span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white uppercase mb-4">
              Audio <span className="text-green-400">Bidirectionnel</span>
            </h1>
            <p className="text-lg text-white/50 mb-6 leading-relaxed">
              Les caméras Superior intègrent 2 microphones et un haut-parleur pour une communication 
              en temps réel. Dissuadez les intrus à distance sans équipement supplémentaire.
            </p>
            <a href="#devis" className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-500/90 text-black font-semibold rounded-lg transition-colors">
              Demander un devis
            </a>
          </div>
          <div className="rounded-xl overflow-hidden">
            <img src={images.audio} alt="Audio bidirectionnel" className="w-full h-auto" />
          </div>
        </div>
      </section>

      {/* Section: Caractéristiques audio */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-16">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-white uppercase mb-8 text-center">
          Dissuasion active avec audio intégré
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white/[0.02] border border-white/5 rounded-xl p-6 text-center">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <Mic className="w-8 h-8 text-green-400" />
            </div>
            <h4 className="text-white font-semibold text-lg mb-2">2 Microphones intégrés</h4>
            <p className="text-sm text-white/50">
              Captent un son clair et sans bruit, fournissant le contexte complet de chaque événement.
            </p>
          </div>
          <div className="bg-white/[0.02] border border-white/5 rounded-xl p-6 text-center">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <Speaker className="w-8 h-8 text-green-400" />
            </div>
            <h4 className="text-white font-semibold text-lg mb-2">Haut-parleur intégré</h4>
            <p className="text-sm text-white/50">
              Dans la boîte de jonction pour dissuader les intrus dès qu'une menace se présente.
            </p>
          </div>
          <div className="bg-white/[0.02] border border-white/5 rounded-xl p-6 text-center">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <Radio className="w-8 h-8 text-green-400" />
            </div>
            <h4 className="text-white font-semibold text-lg mb-2">Entrées/Sorties audio</h4>
            <p className="text-sm text-white/50">
              Connectez des équipements audio supplémentaires si nécessaire via les ports dédiés.
            </p>
          </div>
        </div>
      </section>

      {/* Section: Cas d'usage */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-16">
        <div className="bg-gradient-to-br from-green-900/30 to-fgs-surface border border-green-500/20 rounded-2xl p-6 md:p-10">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white uppercase mb-6">
            Cas d'utilisation
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h4 className="text-white font-semibold mb-4">Dissuasion d'intrusion</h4>
              <ul className="space-y-3">
                {[
                  "Avertissement vocal immédiat à un intrus détecté",
                  "Message automatique ou intervention manuelle",
                  "Effet de surprise pour stopper l'intrusion",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-white/50 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Communication avec visiteurs</h4>
              <ul className="space-y-3">
                {[
                  "Accueil des visiteurs à distance (portail, entrée)",
                  "Instructions pour les livreurs",
                  "Assistance en temps réel depuis l'application",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-white/50 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Section: Intégration */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-16">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div className="rounded-xl overflow-hidden">
            <img src={images.integration} alt="Intégration filaire" className="w-full h-auto" />
          </div>
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white uppercase mb-4">
              Intégration avec équipements tiers
            </h2>
            <p className="text-white/50 mb-6 leading-relaxed">
              Les entrées et sorties d'alarme permettent de connecter directement des détecteurs, 
              stroboscopes, sirènes ou serrures de portes tiers. Les ports audio permettent de 
              connecter les microphones et haut-parleurs existants.
            </p>
            <ul className="space-y-3">
              {[
                "Entrées/sorties alarme pour détecteurs tiers",
                "Ports audio pour équipements existants",
                "Automatisation de l'enregistrement",
                "Réponse coordonnée aux événements",
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span className="text-white text-sm">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Formulaire */}
      <section id="devis" className="max-w-[1400px] mx-auto px-4 md:px-12 scroll-mt-24">
        <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-6 md:p-10">
          <div className="grid lg:grid-cols-2 gap-8">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-white uppercase mb-4">
                Protégez et communiquez
              </h2>
              <p className="text-white/50 mb-6">
                Toutes les caméras Superior intègrent l'audio bidirectionnel pour une protection active.
              </p>
              <div className="space-y-3">
                {[
                  "2 microphones + haut-parleur intégré",
                  "Entrées/sorties audio supplémentaires",
                  "Communication via application Ajax",
                  "Garantie 4 ans FGS",
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span className="text-white text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <QuoteForm category="video" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default TechAudioPage;
